// Header behaviour: the mobile menu (a native <dialog>), the desktop mega menus (open on hover or from their chevron
// button, close on Escape, pointer leave, focus leaving or a click elsewhere), handing the cart icon to the cart drawer
// when there is one, and keeping the cart count and its label in step with cart changes.
import { openDialog, setupDialog } from '@purolabs/dialog';

class Header extends HTMLElement {
  #events;

  connectedCallback() {
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.menu = this.querySelector('#pl-mobile-menu');
    this.cart = this.querySelector('[data-open-cart]');
    this.count = this.querySelector('[data-cart-count]');

    setupDialog(this.menu, signal);

    this.addEventListener('click', (event) => {
      const menuButton = event.target.closest('[data-open-menu]');
      if (menuButton) openDialog(this.menu, menuButton);
      if (event.target.closest('[data-close-menu]')) this.menu?.close();
      const toggle = event.target.closest('[data-mega-toggle]');
      if (toggle) this.#toggle(toggle.closest('.pl-nav__item'));
      // Cart icon opens the drawer if a drawer is listening; otherwise it's a normal link to the cart page
      const cartLink = event.target.closest('[data-open-cart]');
      if (cartLink && document.querySelector('pl-cart-drawer')) {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('purolabs:cart:open'));
      }
    }, { signal });

    for (const item of this.querySelectorAll('.pl-nav__item--panel')) {
      // Touch taps also fire pointerenter; on touch the link navigates and the chevron opens the panel
      item.addEventListener('pointerenter', (event) => event.pointerType !== 'touch' && this.#open(item, 'hover'), { signal });
      item.addEventListener('pointerleave', (event) => event.pointerType !== 'touch' && this.#close(item), { signal });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.#close(item);
      }, { signal });
    }

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const item = this.querySelector('.pl-nav__item[data-open]');
      if (!item) return;
      const hadFocus = item.contains(document.activeElement);
      this.#close(item);
      if (hadFocus) item.querySelector('[data-mega-toggle]')?.focus();
    }, { signal });

    // Escape also dismisses a panel opened by hover while focus is elsewhere on the page (WCAG 1.4.13)
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.contains(event.target)) this.#closeAll();
    }, { signal });

    document.addEventListener('click', (event) => {
      if (!event.target.closest?.('.pl-nav__item')) this.#closeAll();
    }, { signal });

    // Theme editor: selecting a mega menu or menu card block shows its panel
    document.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest?.('.pl-nav__item--panel');
      if (item && this.contains(item)) this.#open(item);
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => this.#closeAll(), { signal });

    document.addEventListener('shopify:cart:lines-update', async (event) => {
      const result = await event.promise?.catch(() => null);
      if (result?.cart) this.#setCount(result.cart.totalQuantity);
    }, { signal });
    document.addEventListener('purolabs:cart:updated', (event) => this.#setCount(event.detail?.cart?.item_count), { signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
  }

  // data-open="hover" opens after a short delay (CSS) so passing over the menu doesn't flash panels; a click or key opens at once
  #open(item, how = 'click') {
    for (const other of this.querySelectorAll('.pl-nav__item[data-open]')) if (other !== item) this.#close(other);
    item.dataset.open = how;
    item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'true');
  }

  #close(item) {
    item.removeAttribute('data-open');
    item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  #closeAll() {
    for (const item of this.querySelectorAll('.pl-nav__item[data-open]')) this.#close(item);
  }

  // A mouse click on the chevron of a panel already opened by hover keeps it open rather than closing it under the pointer
  #toggle(item) {
    if (item.dataset.open === 'click') this.#close(item);
    else this.#open(item);
  }

  #setCount(count) {
    if (!this.count || !Number.isFinite(count)) return;
    this.count.textContent = count;
    this.cart?.setAttribute('aria-label', `${this.cart.dataset.label}, ${count} ${count === 1 ? 'item' : 'items'}`);
  }
}

if (!customElements.get('pl-header')) customElements.define('pl-header', Header);
